import React, { FC, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { state } from "../../index";
import * as actions from "../../store/actions/customersActions";
import customersState from "../../store/reducers/customersState";
import { Customer } from "../auth/Auth";
import Loader from "../UI/Loader";
import Button from "../UI/Button";
import TablePaginationActions from "../UI/TablePaginationActions";
import Container from "react-bootstrap/Container";
import { IconButton, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, TablePagination, Paper, Box } from "@mui/material";
import { Delete, Edit, DomainAdd, CheckCircle, Cancel } from "@mui/icons-material";
import DeleteCustomerModal from "./Modals/DeleteCustomerModal";
import CustomerModal from "./Modals/CustomerModal";

const Customers: FC = () => {
    const [openCustomerModal, setOpenCustomerModal] = useState<boolean>(false);
    const [openDeleteCustomerModal, setOpenDeleteCustomerModal] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

    const dispatch = useDispatch();
    const { fetchCustomers } = bindActionCreators(actions, dispatch);

    const customersState: customersState = useSelector((state: state) => state.customers);
    const customers: Customer[] = customersState.customers;

    useEffect(() => {
        fetchCustomers();
    }, []);

    const handleChangePage = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleOpenEditCustomerModal = (customer: Customer): void => {
        setOpenCustomerModal(true);
        setSelectedCustomer(customer);
    };

    const handleOpenDeleteCustomerModal = (customer: Customer): void => {
        setOpenDeleteCustomerModal(true);
        setSelectedCustomer(customer);
    };

    if (customersState.loading) return <Loader />;

    return (
        <Container style={{ marginTop: 40 }}>
            <Divider sx={{ mb: 1 }}>
                <h3 className="main_title">Kompanije</h3>
            </Divider>

            <Box sx={{ textAlign: "right", mb: 3 }}>
                <Button
                    label="Dodaj kompaniju"
                    startIcon={<DomainAdd />}
                    className="btn_success"
                    handleClick={() => {
                        setOpenCustomerModal(true);
                        setSelectedCustomer(null);
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#E6B238" }}>
                            <TableCell sx={{ color: "#fff" }}>Naziv</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Adresa</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Telefon</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Vreme dostave</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Isti dan</TableCell>
                            <TableCell sx={{ color: "#fff", width: 150 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(rowsPerPage > 0
                            ? customers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : customers
                        ).map(customer => (
                            <TableRow
                                key={customer.id}
                                sx={{
                                    "&:nth-of-type(odd)": {
                                        backgroundColor: "rgba(0,0,0,0.04)"
                                    },
                                    "&:last-child td, &:last-child th": { border: 0 }
                                }}
                            >
                                <TableCell>{customer.name}</TableCell>
                                <TableCell>{customer.address}</TableCell>
                                <TableCell>{customer.phoneNumber}</TableCell>
                                <TableCell>{customer.deliveryTime}</TableCell>
                                <TableCell>
                                    {customer.canOrderCurrentDay ? (
                                        <CheckCircle sx={{ color: "green" }} />
                                    ) : (
                                        <Cancel sx={{ color: "red" }} />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        sx={{ ml: 1 }}
                                        size="small"
                                        onClick={() => handleOpenEditCustomerModal(customer)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        sx={{ ml: 1 }}
                                        size="small"
                                        className="btn_delete"
                                        onClick={() => handleOpenDeleteCustomerModal(customer)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                labelRowsPerPage="Redova po stranici"
                                count={customers.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                colSpan={6}
                                rowsPerPageOptions={[5, 10, 25, { label: "Svi", value: -1 }]}
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}–${to} od ${count !== -1 ? count : `više od ${to}`}`
                                }
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>

            {openCustomerModal &&
                <CustomerModal
                    open={openCustomerModal}
                    selectedCustomer={selectedCustomer}
                    onHandleClose={() => setOpenCustomerModal(false)}
                />}

            <DeleteCustomerModal
                open={openDeleteCustomerModal}
                selectedCustomer={selectedCustomer}
                onHandleClose={() => setOpenDeleteCustomerModal(false)}
            />
        </Container>
    );
};

export default Customers;